import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Lock, ShoppingCart, FileText, BookOpen } from "lucide-react";
import { api } from "@/lib/api";
import { t } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export const PurchaseSection = ({ slug, token }) => {
  const [preview, setPreview] = useState(null);
  const [pricing, setPricing] = useState(null);
  const [company, setCompany] = useState(null);
  const [buying, setBuying] = useState(false);
  const [consent, setConsent] = useState(false);
  const [asCompany, setAsCompany] = useState(false);
  const [vatNumber, setVatNumber] = useState("");
  const [vatResult, setVatResult] = useState(null);
  const [vatChecking, setVatChecking] = useState(false);

  useEffect(() => {
    api.get(`/admin/${slug}/${token}/preview`).then(({ data }) => setPreview(data)).catch(() => {});
    api.get(`/pricing`).then(({ data }) => setPricing(data)).catch(() => {});
    api.get(`/legal/company`).then(({ data }) => setCompany(data.company)).catch(() => {});
  }, [slug, token]);

  // Pre-check the VAT number against VIES so the buyer knows, before paying, whether the
  // sale is reverse charge or falls back to a consumer sale. Debounced.
  useEffect(() => {
    if (!asCompany || vatNumber.trim().length < 4) { setVatResult(null); return; }
    const h = setTimeout(async () => {
      setVatChecking(true);
      try {
        const { data } = await api.post(`/vat/check`, { vat_number: vatNumber.trim() });
        setVatResult(data);
      } catch {
        setVatResult({ valid: false, checked: false });
      } finally {
        setVatChecking(false);
      }
    }, 700);
    return () => clearTimeout(h);
  }, [asCompany, vatNumber]);

  const buy = async () => {
    if (!consent) return;
    if (asCompany && !vatNumber.trim()) {
      toast.error(t("purchase.vatRequired"));
      return;
    }
    setBuying(true);
    try {
      const { data } = await api.post(`/admin/${slug}/${token}/checkout`, {
        origin_url: window.location.origin,
        withdrawal_consent: true,
        buy_as_company: asCompany,
        vat_number: asCompany ? vatNumber.trim() : null,
      });
      if (data.already_unlocked) {
        window.location.reload();
        return;
      }
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("purchase.err.checkoutFailed"));
      setBuying(false);
    }
  };

  if (!preview) return null;

  return (
    <div className="mt-6 rounded-3xl border border-[#E8DEC2] bg-white p-6 shadow-sm" data-testid="purchase-section">
      <div className="flex items-center gap-2 text-[#C86D51] mb-1">
        <BookOpen className="w-5 h-5" />
        <span className="text-sm font-semibold tracking-wide uppercase">{t("purchase.previewEyebrow")}</span>
      </div>
      <h2 className="font-serif text-2xl text-[#2C221E]">{t("purchase.previewTitle")}</h2>
      <p className="text-sm text-[#7A6B63] mt-1">
        {t("purchase.previewSubtitle")}
      </p>

      {/* Preview "pages" */}
      <div className="mt-6 space-y-4" data-testid="book-preview">
        {/* Title page */}
        <div className="rounded-2xl border border-[#E8DEC2] bg-[#FDFBF7] p-6 text-center">
          <div className="text-xs uppercase tracking-widest text-[#C86D51]">Portwrite</div>
          <div className="font-serif text-3xl text-[#2C221E] mt-2">{preview.person_name}</div>
          <div className="text-[#7A6B63] mt-1">{preview.occasion_title}</div>
        </div>

        {/* Portrait excerpt, fades out mid-sentence */}
        <div className="relative rounded-2xl border border-[#E8DEC2] bg-[#F7F2EC] p-6" data-testid="preview-excerpt">
          <p className="font-serif text-[17px] leading-loose text-[#2C221E] whitespace-pre-wrap">
            {preview.portrait_excerpt}
          </p>
          <div className="pointer-events-none absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-[#F7F2EC] to-transparent rounded-b-2xl" />
        </div>

        {/* Questions page */}
        {preview.questions?.length > 0 && (
          <div className="rounded-2xl border border-[#E8DEC2] bg-white p-6" data-testid="preview-questions">
            <h3 className="font-serif text-lg text-[#2C221E] mb-3">{t("purchase.questionsTitle")}</h3>
            <ul className="space-y-2 text-[15px] text-[#4A3E39]">
              {preview.questions.map((q, i) => (
                <li key={i} className="flex gap-2">
                  <span className="text-[#C86D51]">{i + 1}.</span> {q}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* First 3 contributor pages */}
        {preview.preview_contributors?.map((c, i) => (
          <div key={i} className="rounded-2xl border border-[#E8DEC2] bg-white p-6" data-testid={`preview-contributor-${i}`}>
            <div className="font-serif text-lg text-[#2C221E] mb-2">{c.name}</div>
            <div className="space-y-2 text-[15px] text-[#4A3E39]">
              {Object.values(c.answers || {}).filter(Boolean).slice(0, 2).map((a, j) => (
                <p key={j} className="leading-relaxed">{a}</p>
              ))}
            </div>
          </div>
        ))}

        {/* Locked remainder — dummy blurred placeholder content only */}
        {preview.hidden_pages > 0 && (
          <div className="relative rounded-2xl border border-[#E8DEC2] bg-white overflow-hidden" data-testid="preview-locked">
            <div aria-hidden className="p-6 space-y-3 blur-sm select-none opacity-60">
              <div className="h-4 w-1/3 rounded bg-[#E8DEC2]" />
              <div className="h-3 w-full rounded bg-[#EFE7DA]" />
              <div className="h-3 w-11/12 rounded bg-[#EFE7DA]" />
              <div className="h-3 w-4/5 rounded bg-[#EFE7DA]" />
              <div className="h-3 w-full rounded bg-[#EFE7DA]" />
              <div className="h-3 w-2/3 rounded bg-[#EFE7DA]" />
            </div>
            <div className="absolute inset-0 grid place-items-center bg-white/40">
              <div className="flex flex-col items-center text-center px-4">
                <div className="w-11 h-11 rounded-full bg-[#F8EBE6] grid place-items-center">
                  <Lock className="w-5 h-5 text-[#B35A3E]" />
                </div>
                <div className="mt-2 font-serif text-lg text-[#2C221E]" data-testid="hidden-pages-label">
                  {t("purchase.morePages", { n: preview.hidden_pages })}
                </div>
                <div className="text-sm text-[#7A6B63]" data-testid="total-pages-label">
                  {t("purchase.totalPages", { n: preview.total_pages })}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Purchase */}
      <div className="mt-6 pt-6 border-t border-[#E8DEC2]">
        <div className="mb-4 text-xs text-[#7A6B63] leading-relaxed" data-testid="checkout-seller-details">
          {company && (
            <>
              {t("purchase.sellerPre")} <strong>{company.name}</strong> {t("purchase.sellerTradingAs", { cif: company.cif })}{" "}
              <a href={`mailto:${company.email}`} className="hover:text-[#B35A3E] hover:underline">{company.email}</a>
            </>
          )}
        </div>
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div>
            <div className="font-serif text-2xl text-[#2C221E]" data-testid="price-label">
              {pricing ? pricing.price_label : ""}
            </div>
            <div className="text-sm text-[#7A6B63]" data-testid="vat-note">{pricing ? pricing.vat_note : ""}</div>
            <div className="mt-2 flex items-center gap-3 text-xs text-[#7A6B63]">
              <span className="inline-flex items-center gap-1"><FileText className="w-3.5 h-3.5" /> {t("purchase.includesPortrait")}</span>
              <span className="inline-flex items-center gap-1"><BookOpen className="w-3.5 h-3.5" /> {t("purchase.includesWeb")}</span>
            </div>
          </div>
          <Button
            onClick={buy}
            disabled={buying || !consent}
            className="h-12 px-7 bg-[#C86D51] hover:bg-[#B35A3E] text-white rounded-xl text-base disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="purchase-button"
          >
            <ShoppingCart className="w-5 h-5 mr-2" />
            {buying ? t("purchase.redirecting") : t("purchase.buy")}
          </Button>
        </div>

        <div className="mt-4 flex items-start gap-3" data-testid="withdrawal-consent">
          <Checkbox
            id="withdrawal-consent-checkbox"
            checked={consent}
            onCheckedChange={(v) => setConsent(v === true)}
            className="mt-0.5 border-[#C86D51] data-[state=checked]:bg-[#C86D51]"
            data-testid="withdrawal-consent-checkbox"
          />
          <label htmlFor="withdrawal-consent-checkbox" className="text-sm text-[#4A3E39] leading-relaxed cursor-pointer">
            {t("purchase.consentMain")}
            <span className="block mt-1 text-xs text-[#7A6B63]">
              {t("purchase.consentAcceptPre")}{" "}
              <Link to="/kayttoehdot" target="_blank" className="text-[#B35A3E] hover:underline">{t("purchase.consentTerms")}</Link>
              {" "}{t("purchase.consentAnd")}{" "}
              <Link to="/tietosuoja" target="_blank" className="text-[#B35A3E] hover:underline">{t("purchase.consentPrivacy")}</Link>.
            </span>
          </label>
        </div>
        <div className="mt-5 rounded-2xl bg-[#F7F2EC] border border-[#E8DEC2] p-4" data-testid="company-purchase-block">
          <div className="flex items-start gap-3">
            <Checkbox
              id="buy-as-company"
              checked={asCompany}
              onCheckedChange={(v) => setAsCompany(v === true)}
              className="mt-0.5 border-[#C86D51] data-[state=checked]:bg-[#C86D51]"
              data-testid="buy-as-company-checkbox"
            />
            <label htmlFor="buy-as-company" className="text-sm text-[#4A3E39] cursor-pointer">
              {t("purchase.companyToggle")}
            </label>
          </div>
          {asCompany && (
            <div className="mt-3 space-y-2">
              <Label htmlFor="vat-number" className="text-[#2C221E]">{t("purchase.vatLabel")}</Label>
              <Input
                id="vat-number"
                value={vatNumber}
                onChange={(e) => setVatNumber(e.target.value)}
                placeholder={t("purchase.vatPlaceholder")}
                className="h-11 rounded-xl border-[#E8DEC2] bg-white"
                data-testid="vat-number-input"
              />
              {vatChecking && (
                <p className="text-sm text-[#7A6B63]" data-testid="vat-checking">{t("purchase.vatChecking")}</p>
              )}
              {!vatChecking && vatResult && vatResult.valid && (
                <p className="text-sm text-[#3f6047]" data-testid="vat-valid">
                  {t("purchase.vatValid", { price: pricing ? pricing.price_company : "79" })}
                </p>
              )}
              {!vatChecking && vatResult && !vatResult.valid && vatNumber.trim().length >= 4 && (
                <p className="text-sm text-[#B35A3E]" data-testid="vat-invalid">{t("purchase.vatInvalid")}</p>
              )}
            </div>
          )}
          {!asCompany && (
            <p className="mt-2 text-xs text-[#7A6B63]">{t("purchase.companyNote")}</p>
          )}
        </div>
      </div>
    </div>
  );
};
